/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/importStatus.scss';

import React, {MouseEventHandler, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, CloseButton, CustomAppEvent, WithEvents} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {AccountStatus} from '../../util/interfaces';

import classNames from 'classnames';

import {getCurrentError, getCurrentStatus} from '../account/pinger/statusPinger';

import WarnIcon from '../icon/warnIcon';

import {AppContext} from '../misc/appContext';

import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props {
    onDismissImportInfo: MouseEventHandler;
}

const now = IDate.now();

export default function ImportStatus(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {onDismissImportInfo} = props;
    const [status, setStatus] = useState(
        `${t('dashboard.importStatus.updated', 'Bijgewerkt')}, ${t(
            'dashboard.importStatus.lastCheck',
            'laatste controle'
        )} ${now.format('HH:mm')}`
    );
    const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(AccountStatus.IDLE);
    const event: CustomAppEvent = {eventName: 'onPingStatusEvent', handler: onPingStatusEvent};
    const showImportInfo = context.userContext.user?.getFlex('import_info_shown') !== true;
    const statusIcon = useMemo(() => {
        let iconName: IconName | null = null;
        if (accountStatus === AccountStatus.IDLE) {
            if (showImportInfo) return <WarnIcon />;
            iconName = 'check';
        } else if (accountStatus === AccountStatus.IMPORTING) iconName = 'sync-alt';
        else if (accountStatus === AccountStatus.ERROR) iconName = 'exclamation-circle';
        else iconName = 'ban';
        if (!iconName) return null;
        return <AwesomeIcon name={iconName} className='status-icon' />;
    }, [accountStatus, showImportInfo]);

    function onPingStatusEvent() {
        const status: AccountStatus | null = getCurrentStatus(),
            error: string = getCurrentError();
        if (status) setData(status, error);
        else setAccountStatus(null);
    }

    function setData(status: AccountStatus, error: string) {
        setAccountStatus(status);
        const dateLabel = `, ${t('dashboard.importStatus.lastCheck', 'laatste controle')} ${IDate.now().format(
            'HH:mm'
        )}`;
        let nextStatus: string;
        if (status === AccountStatus.ERROR)
            nextStatus = error || t('dashboard.importStatus.error', 'Probleem bij controleren op nieuwe orders');
        else if (status === AccountStatus.IDLE)
            if (showImportInfo)
                nextStatus = t(
                    'dashboard.importStatus.searchTip',
                    'Via de zoekbalk kun je óók orders ophalen via het ordernummer die van vóór het begin gebruik van Bolmate zijn, tot wel 2 jaar terug.'
                );
            else nextStatus = t('dashboard.importStatus.updated', 'Bijgewerkt') + dateLabel;
        else if (status === AccountStatus.IMPORTING)
            nextStatus = t('dashboard.importStatus.importing', 'Controleren op nieuwe orders') + dateLabel;
        else nextStatus = t('dashboard.importStatus.notConnected', 'Niet verbonden met Bol');
        setStatus(nextStatus);
    }

    useEffect(() => {
        onPingStatusEvent();
    }, [context.userContext.user]);

    const isDismissible = accountStatus === AccountStatus.IDLE && showImportInfo;
    return (
        <WithEvents events={event}>
            {accountStatus === null ? null : (
                <div className={classNames('status-row', accountStatus, isDismissible && 'dismiss')}>
                    {statusIcon}
                    <label className='status-label'>{status}</label>
                    {isDismissible && <CloseButton onClick={onDismissImportInfo} />}
                </div>
            )}
        </WithEvents>
    );
}
